import { BidderAdapter } from '@pubstack/common/src/adm/bidder-adapter'
import { PrebidConfig } from '@pubstack/common/src/prebidConfig'
import { Site } from '@pubstack/common/src/site'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { FunctionComponent, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { Skeleton } from '~/components/Skeleton'
import { Status } from '~/components/Status'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import { TableCollapsibleRow, TableCollapsibleRowChevronColumn } from '~/components/table/TableCollapsibleRow'
import { ModuleHeader } from '~/feature-components/ModuleHeader'
import { PureAdmPrebidSiteConfigForm } from './PureAdmPrebidSiteConfigForm'

type PureAdmPrebidIntegrationPageProps = {
  isLoading: boolean
  prebidConfig: PrebidConfig
  onCreateSiteConfig: (siteConfig: AdmSiteConfig) => Promise<void>
  onSaveApsAccountId: (apsAccountId: string) => Promise<void>
  baseCDNUrl: string
  bidderAdapters: BidderAdapter[]
}

export const PureAdmPrebidIntegrationPage: FunctionComponent<PureAdmPrebidIntegrationPageProps> = ({ isLoading, prebidConfig, onCreateSiteConfig, onSaveApsAccountId, baseCDNUrl, bidderAdapters }) => {
  const [columns, setColumns] = useState<TableColumns<Site>>([
    {
      name: 'Site',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    TableCollapsibleRowChevronColumn,
  ])

  /* The APS account ID and every media's wrapper settings are what the auctions on this scope run under,
     so a `user` reads the page and an owner changes it. The per-media forms carry their own gate; this
     one is for the account ID above them. */
  const { canEdit, disabledReason } = useScopePermissions()

  const [isOpen, setIsOpen] = useState<{ [key: string]: boolean }>({})
  // `canEdit &&` because the field opens in edit mode when no ID has been set yet — for a `user` that
  // would be an editable input they cannot save.
  const [isEditingApsAccountId, setIsEditingApsAccountId] = useState<boolean>(canEdit && !prebidConfig.apsAccountId)

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<PrebidConfig>({
    defaultValues: prebidConfig,
  })

  const onApsAccountIdEdit: SubmitHandler<PrebidConfig> = () => {
    setIsEditingApsAccountId(true)
  }

  const onApsAccountIdSubmit: SubmitHandler<PrebidConfig> = (formValue) => {
    onSaveApsAccountId(formValue.apsAccountId)
    setIsEditingApsAccountId(false)
  }

  const sortedSites = handleTableSort(columns, prebidConfig.sites)

  useEffect(() => {
    reset({
      ...prebidConfig,
    })
    // oxlint-disable-next-line react/set-state-in-effect -- same sync: the edit mode follows the config that was just reset into the form
    setIsEditingApsAccountId(canEdit && !prebidConfig.apsAccountId)
  }, [prebidConfig, canEdit])

  return (
    <div className={'flex flex-col gap-5 py-5'}>
      <ModuleHeader isLoading={isLoading} baseCDNUrl={baseCDNUrl} status={<Status state="active">Active</Status>} catalogItemType={'prebidWrapper'} catalogItemCode={'prebid'} />
      <ContentCard color={Colors.Pumpkin}>
        <form className={'inline-flex items-center gap-4'} onSubmit={handleSubmit(onApsAccountIdSubmit)}>
          <Input disabled={!isEditingApsAccountId} type={'text'} name={'apsAccountId'} label={'APS account ID'} control={control} />
          <Tooltip title={disabledReason} disabled={canEdit}>
            {!isEditingApsAccountId ? (
              <Button disabled={!canEdit} variant={'primary'} onClick={handleSubmit(onApsAccountIdEdit)} trackingLabel={'adm-integrations-prebid-aps-account-id-edit'}>
                Edit
              </Button>
            ) : (
              <Button disabled={!isDirty || !canEdit} variant={'primary'} onClick={handleSubmit(onApsAccountIdSubmit)} trackingLabel={'adm-integrations-prebid-aps-account-id-save'}>
                Save
              </Button>
            )}
          </Tooltip>
        </form>
      </ContentCard>
      <div className={'flex flex-col gap-2'}>
        {!isLoading ? (
          prebidConfig.sites.length > 0 ? (
            <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
              {sortedSites.map((site) => {
                /* A media has no config until something is saved against it — creating one, in bulk or
                   otherwise, does not write one. Falling back to an empty config rather than showing
                   nothing gives the same defaulted form the media's own wrappers tab shows, and saving it
                   is what creates the config. Same fallback shape as `GamAndWrappers`. */
                const siteConfig = prebidConfig.siteConfigs.find((s) => site.id === s.siteId) ?? { scopeId: prebidConfig.scopeId, siteId: site.id }
                return (
                  <TableCollapsibleRow
                    key={site.id}
                    isOpen={isOpen[site.id] ?? false}
                    onCollapse={{
                      onClick: () => {
                        setIsOpen((previousState) => ({ ...previousState, [site.id]: !previousState[site.id] }))
                      },
                      trackingLabel: 'adm-prebid-settings-collapse-apply',
                    }}
                    content={() => <PureAdmPrebidSiteConfigForm siteConfig={siteConfig} onCreateSiteConfig={onCreateSiteConfig} bidderAdapters={bidderAdapters} />}
                  >
                    <TableCell className={'font-bold'}>{site.name}</TableCell>
                  </TableCollapsibleRow>
                )
              })}
            </Table>
          ) : (
            <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'You don’t have any sites yet.'} />
          )
        ) : (
          <Skeleton bigger width={'100%'} />
        )}
      </div>
    </div>
  )
}
